import { IImage } from "@faire/web-api/indigofair/data/IImage";
import { IProductVideo } from "@faire/web-api/indigofair/video/IProductVideo";
import { isFastly } from "@faire/web/common/images/optimizeImages";
import { fulfilledOrStale } from "@faire/web/common/instanceUtils";
import { makeObservable } from "@faire/web/common/makeObservable";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import isEmpty from "lodash/isEmpty";
import { action, computed, observable } from "mobx";
import { IPromiseBasedObservable } from "mobx-utils";

import { PLACEHOLDER_IMAGE_URL } from "@faire/retailer/consts/PLACEHOLDER_IMAGE_URL";
import { filterInvalidImages } from "@faire/retailer/lib/filterInvalidImages";
import {
  fromPromiseWithOldValue,
  fromPromise_resolve,
} from "@faire/retailer/lib/mobxHelpers";
import { canShowVideoProducts } from "@faire/retailer/views/product/ProductImages/showProductVideos";
import { IShowroomProductMedia } from "@faire/retailer/views/product/ProductImages/utils";

const PLACEHOLDER_IMAGE = IImage.build({
  token: "p_placeholder",
  url: PLACEHOLDER_IMAGE_URL,
});

export class ProductImageOrVideoViewState {
  constructor(images?: IImage[], videos?: IProductVideo[]) {
    makeObservable(this);
    // Initialize the images set with the images we can guarantee synchronously,
    // to improve the odds of displaying something during SSR.
    const internal = images?.filter((i) => i.url && isFastly(i.url));
    if (internal?.length) {
      this.validateImagesResult = fromPromise_resolve(internal);
    }

    if (videos && canShowVideoProducts()) {
      this._videos = videos.filter((v) => isNotUndefined(v.video?.url));
    }
  }

  @observable
  private validateImagesResult?: IPromiseBasedObservable<IImage[]>;

  private _videos: IProductVideo[] = [];

  @observable
  private _selectedIndex = 0;

  @computed
  private get itemsCount(): number {
    return this.images.length + this.videos.length;
  }

  @computed
  get selectedIndex(): number {
    return this._selectedIndex;
  }

  @computed
  get isSelectionAnImage(): boolean {
    return this._selectedIndex < this.images.length;
  }

  @computed
  get images(): IImage[] {
    const imagesOrPlaceholder = (imgs: IImage[] | undefined): IImage[] =>
      !imgs || isEmpty(imgs) ? [PLACEHOLDER_IMAGE] : imgs;
    return imagesOrPlaceholder(
      fulfilledOrStale(this.validateImagesResult, Array.isArray)
    ).filter((i) => isNotUndefined(i.url));
  }

  @computed
  get videos(): IProductVideo[] {
    return this._videos;
  }

  getProductMedia = (): IShowroomProductMedia[] => {
    const images = this.images.map((image) => ({
      isVideo: false as const,
      image,
    }));

    const videos = this.videos
      .map((productVideo) => {
        const video = productVideo.video;
        if (video?.thumbnail_image) {
          return {
            isVideo: true as const,
            video,
          };
        } else {
          return undefined;
        }
      })
      .filter(isNotUndefined);

    const [firstImage, ...restImages] = images;
    const [firstVideo, ...restVideos] = videos;
    return [firstImage, firstVideo, ...restImages, ...restVideos]
      .filter(isNotUndefined)
      .map((media, index) => ({ ...media, index }));
  };

  @computed
  get selectedImage(): IImage | undefined {
    if (!this.isSelectionAnImage) {
      return undefined;
    }

    return this.images[this._selectedIndex];
  }

  @computed
  get selectedVideo(): IProductVideo | undefined {
    if (this.isSelectionAnImage) {
      return undefined;
    }

    return this.videos[this._selectedIndex - this.images.length];
  }

  @action
  setImages = (images: IImage[]) => {
    this.validateImagesResult = fromPromiseWithOldValue(
      filterInvalidImages(images),
      this.validateImagesResult
    );
  };

  @action
  setIndex = (index: number) => {
    // index should remain within the images+videos bondaries
    if (index < 0) {
      index = this.itemsCount - 1;
    } else if (index >= this.itemsCount) {
      index = 0;
    }

    this._selectedIndex = index;
  };
}
